/**
 * About Modal Component — NF Tracker
 * 
 * Renders a responsive modal overlay showing application information,
 * version, and terms of use disclaimer.
 */

export function openAboutModal() {
  closeAboutModal();

  const modalOverlay = document.createElement('div');
  modalOverlay.id = 'about-modal-overlay';
  modalOverlay.className = 'qr-modal-overlay'; // Reuse backdrop styles

  const isNext = window.location.pathname.startsWith('/next') || localStorage.getItem('nfce_app_channel') === 'next';
  const targetHref = isNext ? '/' : '/next/';
  const targetIcon = isNext ? 'home' : 'science';
  const targetLabel = isNext ? 'Voltar para Versão Principal' : 'Versão Next (Preview)';
  const targetTitle = isNext ? 'Acessar a versão principal estável' : 'Acessar versão de homologação Next';

  modalOverlay.innerHTML = `
    <div class="qr-modal-card glass-panel" style="max-width: 420px; padding: 2.25rem 1.75rem 1.75rem 1.75rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.5rem; box-shadow: var(--shadow-card);">
      <button id="about-modal-close" class="qr-modal-close-btn" aria-label="Fechar" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.35rem; position: absolute; top: 1rem; right: 1rem;">
        <span class="material-symbols-outlined" style="font-size: 20px;">close</span>
      </button>

      <div class="qr-icon-badge" style="background: var(--bg-surface-dim); border: 1px solid var(--border-color); padding: 0.5rem; display: flex; align-items: center; justify-content: center; width: 68px; height: 68px; border-radius: 1.25rem; margin: 0 auto 1.25rem auto;">
        <img src="/favicon.svg" alt="NF Tracker Logo" style="width: 48px; height: 48px; border-radius: 0.75rem;" />
      </div>

      <div style="margin-bottom: 1.25rem; text-align: center;">
        <h2 style="font-size: 1.5rem; font-weight: 800; color: var(--text-main); margin: 0 0 0.35rem 0;">NF Tracker</h2>
        <div style="display: flex; align-items: center; justify-content: center; gap: 0.4rem;">
          <span style="display: inline-block; background: ${isNext ? 'rgba(59, 130, 246, 0.12)' : 'var(--color-warning-bg)'}; color: ${isNext ? '#3B82F6' : 'var(--color-warning-text)'}; border: 1px solid ${isNext ? 'rgba(59, 130, 246, 0.25)' : 'var(--color-warning-border)'}; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.72rem; text-transform: uppercase;">
            ${isNext ? 'Next Preview' : 'Beta'}
          </span>
          <span id="about-app-version" style="font-size: 0.78rem; font-weight: 700; color: var(--text-muted); font-family: var(--font-mono);">
            v0.26.0
          </span>
        </div>
      </div>

      <div style="background: var(--bg-surface-alt); border: 1px solid var(--border-color); border-radius: 1rem; padding: 1.25rem; margin-bottom: 1.5rem; text-align: left;">
        <div style="display: flex; align-items: flex-start; gap: 0.6rem; margin-bottom: 0.75rem;">
          <span class="material-symbols-outlined" style="color: #10B981; font-size: 20px; flex-shrink: 0; margin-top: 2px;">verified_user</span>
          <span style="font-size: 0.85rem; font-weight: 700; color: var(--text-main); line-height: 1.4;">Controle Inteligente de Gastos Fiscais através da NFC-e</span>
        </div>
        
        <div style="font-size: 0.8rem; color: var(--text-muted); line-height: 1.5; border-top: 1px solid var(--border-color); padding-top: 0.75rem;">
          Aplicativo para uso pessoal e familiar. A responsabilidade pelo uso e pelos dados inseridos é exclusivamente do usuário.
        </div>
      </div>

      <div class="qr-modal-footer" style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.6rem; font-size: 0.75rem; color: var(--text-muted); margin-top: 0.5rem;">
        <div style="display: flex; align-items: center; gap: 0.35rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #10B981;">shield</span>
          <span>Seus dados são mantidos com privacidade e segurança</span>
        </div>
        <a href="${targetHref}" id="about-channel-switch-link" style="display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.74rem; font-weight: 600; color: var(--text-subtle); text-decoration: none; padding: 0.25rem 0.75rem; border-radius: var(--radius-pill); border: 1px solid var(--border-color); background: transparent; transition: all 150ms ease;" onmouseover="this.style.color='var(--text-main)'; this.style.borderColor='var(--border-color-strong)'" onmouseout="this.style.color='var(--text-subtle)'; this.style.borderColor='var(--border-color)'" title="${targetTitle}">
          <span class="material-symbols-outlined" style="font-size: 14px;">${targetIcon}</span>
          <span>${targetLabel}</span>
        </a>
      </div>
    </div>
  `;

  document.body.appendChild(modalOverlay);

  const switchLink = modalOverlay.querySelector('#about-channel-switch-link');
  if (switchLink) {
    switchLink.addEventListener('click', (e) => {
      e.preventDefault();
      try {
        localStorage.setItem('nfce_app_channel', isNext ? 'main' : 'next');
      } catch (err) {}
      window.location.href = targetHref + (window.location.hash || '');
    });
  }

  // Dynamically fetch actual deploy version (unauthenticated public API)
  fetch('/api/version')
    .then(r => r.ok ? r.json() : null)
    .then(data => {
      if (data && data.version) {
        const verEl = modalOverlay.querySelector('#about-app-version');
        if (verEl) {
          verEl.textContent = data.version.startsWith('v') ? data.version : `v${data.version}`;
        }
      }
    })
    .catch(() => {
      fetch('/version.txt')
        .then(r => r.ok ? r.text() : null)
        .then(ver => {
          if (ver) {
            const verEl = modalOverlay.querySelector('#about-app-version');
            if (verEl) verEl.textContent = ver.trim().startsWith('v') ? ver.trim() : `v${ver.trim()}`;
          }
        })
        .catch(() => {});
    });

  requestAnimationFrame(() => {
    modalOverlay.classList.add('active');
  });

  const closeBtn = modalOverlay.querySelector('#about-modal-close');
  if (closeBtn) closeBtn.addEventListener('click', closeAboutModal);

  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      closeAboutModal();
    }
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeAboutModal();
      document.removeEventListener('keydown', handleKeyDown);
    }
  };
  document.addEventListener('keydown', handleKeyDown);
}

export function closeAboutModal() {
  const modal = document.getElementById('about-modal-overlay');
  if (modal) {
    modal.classList.remove('active');
    setTimeout(() => {
      if (modal.parentNode) {
        modal.parentNode.removeChild(modal);
      }
    }, 200);
  }
}

if (typeof window !== 'undefined') {
  window.openAboutModalGlobal = openAboutModal;
}
